import { Need, TextValue, ValueList } from 'rlv-common/types';
import { PaymentMethod, PaymentMethodText } from '../../../helpers/PaymentMethod';
import { NeedInputLabels, NeedInputTooltips } from './UpdatingOfferForm.types';

export const paymentMethodOptions: ValueList<TextValue> = {
  defaultValue: {
    key: PaymentMethod.MONATLICH,
    text: PaymentMethodText.MONATLICH,
  },
  possibleValues: [
    { key: PaymentMethod.MONATLICH, text: PaymentMethodText.MONATLICH },
    {
      key: PaymentMethod.VIERTELJAEHRLICH,
      text: PaymentMethodText.VIERTELJAEHRLICH,
    },
    { key: PaymentMethod.HALBJAEHRLICH, text: PaymentMethodText.HALBJAEHRLICH },
    { key: PaymentMethod.JAEHRLICH, text: PaymentMethodText.JAEHRLICH },
  ],
};

const PAYMENT_METHOD_LABEL = 'Zahlweise';
const PERIOD_IN_YEARS_LABEL = 'Laufzeit';

export const UpdatingOfferInput: {
  [Need.KONSTANT]: NeedInputLabels;
  [Need.DECREASING]: NeedInputLabels;
} = {
  KONSTANT: {
    paymentMethod: { key: 'paymentMethod', text: PAYMENT_METHOD_LABEL },
    sumInsured: { key: 'sumInsured', text: 'Versicherungssumme' },
    periodInYears: { key: 'periodInYears', text: PERIOD_IN_YEARS_LABEL },
  },
  LINEAR_FALLEND: {
    paymentMethod: { key: 'paymentMethod', text: PAYMENT_METHOD_LABEL },
    sumInsured: { key: 'sumInsured', text: 'Versicherungssumme im ersten Jahr' },
    insuranceEndSum: {
      key: 'insuranceEndSum',
      text: 'Versicherungssumme im letzten Jahr',
    },
    periodInYears: { key: 'periodInYears', text: PERIOD_IN_YEARS_LABEL },
  },
};

const CONSTANT_PERIOD_IN_YEARS_TOOLTIP = (
  <div>
    <p>
      <b>So wählen Sie Ihre Laufzeit:</b>
    </p>
    <p>
      Ihre Angehörigen erhalten die Versicherungssumme, falls Sie während der Vertragslaufzeit
      sterben. Wählen Sie eine Laufzeit, bis Ihre Kinder voraussichtlich die Ausbildung beendet
      haben und/oder Ihr Partner mit niedrigeren Belastungen zu rechnen hat. Überlegen Sie, bis zu
      welchem Alter Ihr Partner und/oder Ihre Kinder ganz oder zum Teil auf Ihr Einkommen oder Ihre
      Arbeit zu Hause angewiesen sind. Berücksichtigen Sie auch, bis wann zusätzliche Belastungen
      wie Kredite bestehen und wann die Kinder voraussichtlich finanziell unabhängig sind.
    </p>
    <p>In der Premium-Variante besteht die Möglichkeit, einen bestehenden Vertrag zu verlängern.</p>
  </div>
);

const DECREASING_PERIOD_IN_YEARS_TOOLTIP = (
  <div>
    <p>
      <b>So wählen Sie Ihre Laufzeit:</b>
    </p>
    <p>
      Mit der Laufzeit bestimmen Sie, in welchem Maß Ihre Versicherungssumme im Verlauf sinkt. Die
      Hinterbliebenen erhalten innerhalb dieser Laufzeit die aktuelle Versicherungssumme für das
      Jahr, in dem der Todesfall eintritt. Wählen Sie die Laufzeit so, dass Ihr Darlehen oder Kredit
      in dieser Zeit auf jeden Fall vollständig abbezahlt ist. Berücksichtigen Sie auch, bis wann
      Ihre Kinder voraussichtlich die Ausbildung beendet haben und/oder Ihr Partner mit niedrigeren
      Belastungen zu rechnen hat. Überlegen Sie, bis zu welchem Alter Ihr Partner und/oder Ihre
      Kinder ganz oder zum Teil auf Ihr Einkommen oder Ihre Arbeit zu Hause angewiesen sind. In der
      Premium-Variante besteht die Möglichkeit, einen bestehenden Vertrag zu verlängern.
    </p>
  </div>
);

export const UpdatingOfferTooltips: {
  [Need.KONSTANT]: NeedInputTooltips;
  [Need.DECREASING]: NeedInputTooltips;
} = {
  KONSTANT: {
    sumInsured: (
      <div>
        <p>
          <b>So wählen Sie Ihre Versicherungssumme:</b>
        </p>
        <p>
          Die Versicherungssumme erhalten Ihre Hinterbliebenen, falls Sie während der Laufzeit
          sterben. Die gewählte Summe sollte so hoch sein, dass Ihre Familie oder Ihr Partner im
          Todesfall den Lebensstandard ohne Ihr Einkommen oder Ihre Care-Arbeit gut halten kann.
          Wenn Sie Kinder haben am besten so lange, bis sie finanziell unabhängig sind.
        </p>
        <p>
          <b>Als Faustregel gilt: </b> Rechnen Sie mindestens das 3-fache Ihres jährlichen
          Bruttoeinkommens für Paare und Familien mit älteren Kindern. Für Familien mit kleinen
          Kindern mindestens das 4- bis 5-fache. Überlegen Sie, welche finanziellen Verpflichtungen
          im Todesfall von Ihren Hinterbliebenen übernommen werden müssen.
        </p>
        <p>
          <b>Alleinverdiener: </b> Die Summe sollte ausreichen, damit Ihre Familie den größten Teil
          des Lebensunterhalts ohne Sie bestreiten kann. Witwen- und Waisenrente reichen meist nicht
          aus.
        </p>
        <p>
          <b>Zwei Einkommen: </b> Welche Kosten und finanziellen Belastungen muss Ihr Partner
          weiterhin tragen, wenn Ihr Einkommen wegfällt?
        </p>
        <p>
          <b>Care-Arbeit: </b> Übernehmen Sie z. B. Hausarbeit und Kinderbetreuung, die im Falle des
          Todes übernommen werden müssen, entstehen hierfür jahrelang zusätzliche laufende Kosten.
        </p>
        <p>
          Bei ERGO können Sie Ihre Versicherungssumme auch nachträglich erhöhen, wenn sich Ihre
          Lebenssituation ändert.
        </p>
      </div>
    ),
    periodInYears: CONSTANT_PERIOD_IN_YEARS_TOOLTIP,
  },
  LINEAR_FALLEND: {
    sumInsured: (
      <div>
        <p>
          <b>So wählen Sie Ihre linear fallende Versicherungssumme:</b>
        </p>
        <p>
          Die Hinterbliebenen erhalten die aktuelle Versicherungssumme für das Jahr, in dem der
          Todesfall eintritt. Die gewählte Summe sollte für das erste Jahr mindestens so hoch sein,
          dass Ihre Hinterbliebenen weiterhin z. B. Kredit- und Darlehensraten damit bezahlen
          können, falls Sie sterben. Wenn Sie sichergehen möchten, dass ausreichend Geld vorhanden
          ist, runden Sie die Startsumme auf.
        </p>
        <p>
          Überlegen Sie, welche finanziellen Verpflichtungen im Todesfall von Ihrem Partner und/oder
          den Kindern heute getragen werden müssen und wie hoch der finanzielle Bedarf zum Ende der
          Laufzeit ist. Im Allgemeinen rechnet man mit dem 3- bis 5-fachen des
          Jahresbruttoeinkommens. Oder Sie sichern die Höhe eines offenen Kredits oder Darlehens ab,
          den Ihre Angehörigen abbezahlen müssen.
        </p>
      </div>
    ),
    periodInYears: DECREASING_PERIOD_IN_YEARS_TOOLTIP,
  },
};
